import { lazy, memo, Suspense } from 'react';
import { Outlet, Route, Routes } from 'react-router-dom';
import SocketProvider from 'src/hooks/useSocket';
import LayoutClient from 'src/layouts';
import { SpinnerLoader } from 'src/libraries';
import { Routes as Router } from 'src/routes';
import RouteGuard from 'src/routes/guard';
import InitAppProvider from './initApp.provider';

const Page404Screen = lazy(() => import('src/screens/errors/page404'));
const Page500Screen = lazy(() => import('src/screens/errors/page500'));
const Page403Screen = lazy(() => import('src/screens/errors/page403'));
const Privacy = lazy(() => import('src/screens/privacy/privacy'));
const SupportScreen = lazy(() => import('src/screens/privacy/support'));

const LoginScreen = lazy(() => import('src/screens/auth/login/index'));
const RegisterScreen = lazy(() => import('src/screens/auth/register'));
const VerificationUserScreen = lazy(() => import('src/screens/auth/verify'));
const ResetPasswordScreen = lazy(() => import('src/screens/auth/reset-password'));
const ForgotPasswordScreen = lazy(() => import('src/screens/auth/forgot-password'));

// const ProfileScreen = lazy(() => import('src/screens/profile'));

const DashboardScreen = lazy(() => import('src/screens/dashboard'));

const CloudScreen = lazy(() => import('src/screens/cloud'));

const PlaylistScreen = lazy(() => import('src/screens/playlist/playlist'));
const PlaylistHistoryScreen = lazy(() => import('src/screens/playlist/history'));

const DeviceScreen = lazy(() => import('src/screens/device'));

const AccountScreen = lazy(() => import('src/screens/account'));

const PaymentScreen = lazy(() => import('src/screens/payment'));

const CategoryNewsScreen = lazy(() => import('src/screens/setting/categoryNews'));
const SoundQualityScreen = lazy(() => import('src/screens/setting/soundQuality'));
const IcecastScreen = lazy(() => import('src/screens/setting/icecast'));
const PackageScreen = lazy(() => import('src/screens/setting/package_license'));
const ErrorDefineScreen = lazy(() => import('src/screens/setting/error_define'));
const ConfigScreen = lazy(() => import('src/screens/setting/config'));
const ProvinceScreen = lazy(() => import('src/screens/area/province'));
const CommuneScreen = lazy(() => import('src/screens/area/commune'));
const VillageScreen = lazy(() => import('src/screens/area/village'));

const LogScreen = lazy(() => import('src/screens/log'));

function RouteProvider() {
  return (
    <Routes>
      <Route
        element={
          <InitAppProvider>
            <RouteGuard>
              <SocketProvider>
                <LayoutClient />
              </SocketProvider>
            </RouteGuard>
          </InitAppProvider>
        }
      >
        <Route path={Router.HOME.path} element={<DashboardScreen />} />

        <Route path={Router.CLOUD.path} element={<CloudScreen />} />

        <Route path={Router.PLAYLIST_LIST.path} element={<PlaylistScreen />} />
        <Route path={Router.PLAYLIST_HISTORY.path} element={<PlaylistHistoryScreen />} />

        <Route path={Router.DEVICE.path} element={<DeviceScreen />} />

        <Route path={Router.ACCOUNT.path} element={<AccountScreen />} />

        <Route path={Router.PAYMENT.path} element={<PaymentScreen />} />

        <Route path={Router.SETTING_CATEGORY_NEWS.path} element={<CategoryNewsScreen />} />
        <Route path={Router.SETTING_SOUND_QUALITY.path} element={<SoundQualityScreen />} />
        <Route path={Router.SETTING_ICECAST.path} element={<IcecastScreen />} />
        <Route path={Router.SETTING_PACKAGE.path} element={<PackageScreen />} />
        <Route path={Router.SETTING_ERROR.path} element={<ErrorDefineScreen />} />
        <Route path={Router.SETTING_CONFIG.path} element={<ConfigScreen />} />
        <Route path={Router.SETTING_AREA_PROVINCE.path} element={<ProvinceScreen />} />
        <Route path={Router.SETTING_AREA_COMMUNE.path} element={<CommuneScreen />} />
        <Route path={Router.SETTING_AREA_VILLAGE.path} element={<VillageScreen />} />

        <Route path={Router.LOG.path} element={<LogScreen />} />
      </Route>

      <Route
        element={
          <Suspense fallback={<SpinnerLoader bg={false} />}>
            <Outlet />
          </Suspense>
        }
      >
        <Route path={Router.LOGIN.path} element={<LoginScreen />} />
        <Route path={Router.REGISTER.path} element={<RegisterScreen />} />
        <Route path={Router.VERIFICATION.path} element={<VerificationUserScreen />} />
        <Route path={`${Router.RESET_PASSWORD.path}/:token`} element={<ResetPasswordScreen />} />
        <Route path={Router.FORGOT_PASSWORD.path} element={<ForgotPasswordScreen />} />

        <Route path={Router.PRIVACY.path} element={<Privacy />} />
        <Route path={Router.SUPPORT.path} element={<SupportScreen />} />
        <Route path={Router.PAGE500.path} element={<Page500Screen />} />
        <Route path={Router.PAGE403.path} element={<Page403Screen />} />

        <Route path="*" element={<Page404Screen />} />
      </Route>
    </Routes>
  );
}

export default memo(RouteProvider);
